<template>
  <v-card class="eie-investements">
    <v-card-title class="toolbar primary white--text">
      <h3 class="mr-3">
        Investments ({{ eieInvestments && eieInvestments.length || 0 }})
      </h3>
      <v-spacer />
      <div class="ml-5">
        <v-select
          v-model="searchColumn"
          class="ml-5 mr-3"
          primary
          dark
          :items="searchColumnOptions"
          label="Search by"
          item-text="title"
          item-value="value"
          hide-details
          @change="onChangePagination"
        />
      </div>

      <v-text-field
        v-if="searchColumn === searchColumnOptions[0].value"
        v-model="selectedValue"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        :label="`Search by ${searchColumn}`"
        single-line
        hide-details
      />

      <v-autocomplete
        v-if="searchColumn === searchColumnOptions[1].value"
        v-model="selectedValue"
        :items="statusOptions"
        :label="`Search by ${searchColumn}`"
        primary
        dark
        solo-inverted
        flat
        hide-details
      />

      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            dark
            flat
            icon
            @click="clearEieInvestments().then(onChangePagination)"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh investments</span>
      </v-tooltip>
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <v-text-field
        v-model="search"
        class="ml-3"
        append-icon="search"
        label="Filter results"
        single-line
        hide-details
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredEieInvestments"
      :pagination.sync="pagination"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      hide-actions
      select-all
      item-key="id"
      class="elevation-1"
    >
      <template
        slot="headers"
        slot-scope="props"
      >
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              'column sortable',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : ''
            ]"
            @click="changeSort(header.value)"
          >
            <v-icon small>
              arrow_upward
            </v-icon>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.checkoutId }}
          </td>
          <td class="text-xs-center">
            <UserChip
              :id="parseInt(props.item.userId, 10)"
              :key="`user-chip-${props.item.userId}`"
            />
          </td>
          <td class="text-xs-center">
            {{ props.item.fpTrancheId }}
          </td>
          <td class="text-xs-center">
            {{ props.item.amountInCents / 100 }} €
          </td>
          <td class="text-xs-center">
            <v-chip
              :color="getStatus(props.item.status).color"
              outline
              small
            >
              {{ getStatus(props.item.status).title }}
            </v-chip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  :color="isOlderThen2Weeks(props.item.createdAt).color"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.createdAt) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.createdAt) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <!--
            <v-btn
              flat
              color=""
              @click="cancelInvestment(props.item.id)"
            >
              Cancel
            </v-btn>
            -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  flat
                  icon
                  :color="props.expanded ? 'blue darken-1' : ''"
                  @click="props.expanded = !props.expanded"
                  v-on="on"
                >
                  <v-icon small>
                    {{ props.expanded ? 'visibility_off' : 'visibility' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ props.expanded ? 'Close Investment details' : 'Open Investment details' }}</span>
            </v-tooltip>

            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :disabled="!isCancelableStatus(props.item.status)"
                  :dark="isCancelableStatus(props.item.status)"
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click="_onCancelInvestment(props.item)"
                >
                  <v-icon color>
                    event_busy
                  </v-icon>
                </v-btn>
              </template>
              <span>Cancel Investment</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="props.item.error">
          <td
            colspan="9"
            class="text-xs-left errorTd"
          >
            <div class="errorRow">
              <v-icon color="red">
                info
              </v-icon> {{ props.item.error }}
            </div>
          </td>
        </tr>
      </template>

      <template v-slot:expand="props">
        <v-container
          cols="12"
          class="d-flex grey lighten-2"
          fluid
        >
          <v-card class="ma-2 pa-6">
            <v-card-title class="toolbar primary white--text subheading">
              <v-icon
                dark
                color="white"
              >
                description
              </v-icon>
              <span class="ml-2">Investment details</span>
            </v-card-title>

            <v-card-text>
              <v-layout
                wrap
                class="pa-0 "
              >
                <v-flex
                  v-for="field in cleanFields(props.item)"
                  :key="field"
                  class="pa-2"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-text-field
                          :label="formatFieldName(field, props.item)"
                          :value="formatValue(props.item[field], field)"
                          readonly
                          class="body-1"
                          :class="field"
                        />
                      </span>
                    </template>
                    <span>
                      field: {{ field }}
                    </span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-container>
        <v-container
          cols="12"
          class="d-flex grey lighten-2"
          fluid
        >
          <v-card class="ma-2 pa-6">
            <v-card-title class="toolbar primary white--text subheading">
              <v-icon
                dark
                color="white"
              >
                description
              </v-icon>
              <span class="ml-2">Compliance Data</span>
            </v-card-title>

            <v-card-text>
              <v-layout
                wrap
                class="pa-0 "
              >
                <tr>
                  <td>
                    <v-chip
                      class="custom-chip"
                      color="blue darken-2"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="white"
                        block
                        v-on="on"
                        @click.stop="() => onViewUser(props.item.userId)"
                      >
                        <span class="ml-2">View User</span>
                      </v-btn>
                    </v-chip>
                  </td>
                  <td>
                    <v-chip
                      class="custom-chip"
                      color="blue darken-2"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="white"
                        block
                        v-on="on"
                        @click.stop="() => onViewUserIdent(props.item.userId)"
                      >
                        <span class="ml-2">User Ident</span>
                      </v-btn>
                    </v-chip>
                  </td>
                  <td>
                    <v-chip
                      class="custom-chip"
                      color="blue darken-2"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="white"
                        block
                        v-on="on"
                        @click.stop="() => onViewFp(props.item.fpTrancheId)"
                      >
                        <span class="ml-2">Financial Product</span>
                      </v-btn>
                    </v-chip>
                  </td>
                  <td>
                    <v-chip
                      class="custom-chip"
                      color="blue darken-2"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="white"
                        block
                        v-on="on"
                        @click.stop="() => onDownloadSurveyDocument(props.item.userId, props.item.createdAt)"
                      >
                        <span class="ml-2">Download Survey</span>
                      </v-btn>
                    </v-chip>
                  </td>
                  <td>
                    <v-chip
                      class="custom-chip"
                      color="blue darken-2"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="white"
                        block
                        v-on="on"
                        @click.stop="() => onDownloadSigningDocument(props.item.userId, props.item.id)"
                      >
                        <span class="ml-2">Download Zeichnungsschein</span>
                      </v-btn>
                    </v-chip>
                  </td>
                  <td
                    v-if="props.item.assetOperationId"
                  >
                    <v-chip
                      class="custom-chip"
                      color="blue darken-2"
                    >
                      <v-btn
                        dark
                        flat
                        icon
                        color="white"
                        block
                        v-on="on"
                        @click.stop="() => onViewVaultTransaction(props.item.assetOperationId)"
                      >
                        <span class="ml-2">Vault Transaction</span>
                      </v-btn>
                    </v-chip>
                  </td>
                </tr>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-container>
      </template>
      <template v-slot:footer>
        <td :colspan="headers.length">
          <v-flex
            xs12
            class="footer"
          >
            <v-spacer />
            <v-select
              v-model="pagination.rowsPerPage"
              :items="rowsPerPageItems"
              class="rows-per-page"
              label="Rows per page"
              item-text="text"
              item-value="value"
              @change="onChangePagination"
            />
            <v-pagination
              v-model="pagination.page"
              :length="numberOfPages"
              :total-visible="5"
              @input="onChangePagination"
            />
          </v-flex>
        </td>
      </template>
    </v-data-table>

    <ConfirmDialog
      :dialog="confirmDialogOpen"
      :confirm-btn-label="'Confirm'"
      :cancel-btn-label="'Cancel'"
      :head-text="cancelInvestmentsHeader(selectedInvestment)"
      :description="`Are you sure to cancel the investment: ${ selectedInvestment ? selectedInvestment.id : ''}?`"
      :confirm-action="_onCancelConfirm"
      :cancel-action="_onCancel"
    />
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { setQuery, toQueryString, base64toBlob, saveFile } from '@/helpers';
import EieInvestmentStatus from '@/enum/eie/EieInvestmentStatus';
import UserChip from '@/components/shared/user/UserChip.vue';
import ConfirmDialog from '@/components/shared/ConfirmDialog';

export default {
  name: 'EieInvestmentsTable',
  components: {
    ConfirmDialog,
    UserChip,
  },
  props: {
    eieInvestments: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    cancelInvestment: {
      type: Function,
      default: () => ({}),
    },
  },
  data() {
    return {
      search: '',
      selected: [],
      selectedInvestment: null,
      selectedValue: '',
      filteredEieInvestments: [],
      fetchTimeout: null,
      confirmDialogOpen: false,
      pagination: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'createdAt',
        descending: true,
      },
      searchColumn: '',
      searchColumnOptions: [
        { title: 'User Id', value: 'userId' },
        { title: 'Status', value: 'status' },
      ],
      statusOptions: ['succeeded', 'creation', 'cancelled', 'rejected', 'validation', 'reserving', 'processing', 'cancelling'],
      rowsPerPageItems: [
        {
          text: '5',
          value: 5,
        },
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        created_at: [],
        status: [],
      },
      headers: [
        {
          text: 'Investment Id',
          align: 'left',
          sortable: true,
          value: 'id',
        },
        {
          text: 'Checkout Id',
          align: 'left',
          sortable: true,
          value: 'checkoutId',
        },
        {
          text: 'User Id',
          align: 'left',
          sortable: true,
          value: 'userId',
        },
        {
          text: 'FP Tranche Id',
          align: 'left',
          sortable: true,
          value: 'fpTrancheId',
        },
        {
          text: 'Amount',
          align: 'left',
          sortable: true,
          value: 'amountInCents',
        },
        {
          text: 'Status',
          value: 'status',
          filter: {
            value: true,
            type: 'select',
          },
        },
        {
          text: 'Created At',
          value: 'createdAt',
        },
        {
          text: 'Actions',
          value: '',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'totalElements',
      'hasNextPage',
    ]),
    statuses() {
      return Object.keys(EieInvestmentStatus).map(item => EieInvestmentStatus[item]);
    },
    numberOfPages() {
      return Math.ceil(this.totalElements / this.pagination.rowsPerPage) + (this.hasNextPage ? 1 : 0);
    },
  },
  watch: {
    searchColumn(newVal, oldVal) {
      clearTimeout(this.fetchTimeout);

      if (!!oldVal && !!newVal) {
        this.selectedValue = null;
      }
    },
    search(val) {
      setQuery(
        {
          search: val,
        },
        this.$route,
        this.$router,
      );

      this.filterInvestments();
    },
    selectedValue(newVal) {
      clearTimeout(this.fetchTimeout);

      if (newVal) {
        this.fetchTimeout = setTimeout(async () => {
          this.clearEieInvestments().then(this.onChangePagination());
        }, 500);
      }
    },
    pagination() {
      this.onChangePagination();
    },
  },
  beforeMount() {
    this.pagination = {
      page: Number(this.$route.query.page) || 1,
      rowsPerPage: Number(this.$route.query.rowsPerPage) || 5,
      sortBy: this.$route.query.sortBy || 'createdAt',
      descending: Boolean(this.$route.query.descending) || true,
    };
    this.search = this.$route.query.search || '';
    this.searchColumn = this.$route.query.searchColumn || 'status';
    this.selectedValue = this.$route.query.selectedValue || 'creation';
  },
  mounted() {
    this.filterInvestments();
  },
  methods: {
    ...mapActions(['getEieInvestments', 'clearEieInvestments', 'getFinancialProduct', 'downloadSurveyPdf', 'getSigningDocumentUrl']),
    filterInvestments() {
      this.filteredEieInvestments = this.search ?
        this.eieInvestments.filter(x => JSON.stringify(x).toLowerCase().includes(this.search.toLowerCase())) :
        [...this.eieInvestments];
    },
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    getStatus(status) {
      return this.statuses.find(item => item.status === status) || {};
    },
    isOlderThen2Weeks(createdAt) {
      const twoWeeks = 14 * 24 * 60 * 60 * 1000;

      if ((createdAt + twoWeeks) < Date.now()) {
        return { color: 'red' };
      }

      return { color: 'grey' };
    },
    onChangePagination() {
      // if no values no point in doing new requests
      if (!this.selectedValue || !this.searchColumn) {
        clearTimeout(this.fetchTimeout);

        return;
      }

      console.info('onChangePagination', this.pagination, this.searchColumn, this.selectedValue, this.search);

      if (this.pagination.page === this.numberOfPages && !this.hasNextPage) {
        return;
      }

      setQuery(
        {
          searchColumn: this.searchColumn,
          selectedValue: this.selectedValue,
          search: this.search,
          ...this.pagination,
        },
        this.$route,
        this.$router,
      );

      const { descending, page, rowsPerPage } = this.pagination;

      const params = {
        per_page: rowsPerPage,
        page,
        order: `${descending ? 'desc' : 'asc'}`,
      };

      if (this.searchColumn === 'userId') {
        params.userId = this.selectedValue;
      } else if (this.searchColumn === 'status') {
        params.status = this.selectedValue;
      }

      this.fetchTimeout = setTimeout(async () => {
        await this.getEieInvestments(params);
        this.filterInvestments();
      }, 1000);
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
      this.onChangePagination();
    },
    boda(input) {
      console.info(input);
    },
    async _onCancelConfirm() {
      await this.cancelInvestment(this.selectedInvestment.id);
      await this.clearEieInvestments();

      this.onChangePagination();
      this._onCancel();
    },
    _onCancel() {
      this.confirmDialogOpen = false;
      this.selectedInvestment = null;
    },
    _onCancelInvestment(item) {
      this.selectedInvestment = item;
      this.confirmDialogOpen = true;
    },
    // investment details
    cleanFields(data) {
      return Object.keys(data)
        .filter(field => !!data[field] && typeof data[field] !== 'object');
    },
    cancelInvestmentsHeader(item) {
      const twoWeeks = 14 * 24 * 60 * 60 * 1000;
      if (item && (item.createdAt + twoWeeks) < Date.now()) {
        return 'Cancel investment over 2 weeks old!';
      }

      return 'Cancel investment';
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
      const sentence = _.startCase(camelCaseSentence);

      if (item) {
        if (str === 'security_wallet' && item.sell_offer.asset.provider === 'baaderbank') {
          return `${_.capitalize(sentence)} / Baader Bank account`;
        }
      }

      return _.capitalize(sentence);
    },
    formatValue(value, key) {
      if (key === 'total' || key === 'unit_price') {
        return this.centsToEuros(value);
      }
      if (key.includes('_at')) {
        return this.formatDate(value);
      }

      return value;
    },
    isCancelableStatus(status) {
      const cantelablestatus = ['succeeded', 'creation', 'validation', 'reserving', 'processing'];

      return cantelablestatus.includes(status);
    },
    async onViewUser(userId) {
      const link = document.createElement('a');
      link.href = `/#/users/${userId}`;
      link.target = '_blank';
      link.click();
    },
    async onViewUserIdent(userId) {
      const link = document.createElement('a');
      link.href = `/#/ident-cases?sort=identification_result.created_at&sortOrientation=desc&page=1&itemsPerPage=10&userId=${userId}`;
      link.target = '_blank';
      link.click();
    },
    async onViewFp(fpTrancheId) {
      const { product } = await this.getFinancialProduct(toQueryString({
        id: fpTrancheId,
      }));
      const link = document.createElement('a');
      if (product.stage === 'draft') {
        link.href = `/#/financial-products/draft/${product.instrument}/${product.owner_id}/${product.created_at}`;
      } else {
        link.href = `/#/financial-products/product/${product.instrument}/${product.security_id_type}/${product.security_id}`;
      }
      link.target = '_blank';
      link.click();
    },
    async onDownloadSigningDocument(userId, id) {
      try {
        const { signedUrl } = await this.getSigningDocumentUrl({ userId, id });

        const link = document.createElement('a');
        link.href = signedUrl;
        link.target = '_blank';
        link.click();
      } catch (error) {
        console.error('Error downloading signing document', error);
      }
    },
    async onDownloadSurveyDocument(userId, createdAt) {
      const data = {
        userId,
        createdAt,
      };
      try {
        const response = await this.downloadSurveyPdf(data);

        saveFile(base64toBlob(response.data), `${response.headers['x-file-name']}.pdf`);
      } catch (error) {
        console.error('Error downloading survey document', error);
      }
    },
    async onViewVaultTransaction(assetOperationId) {
      const link = document.createElement('a');
      link.href = `/#/vault/transactions?page=1&search=${assetOperationId}`;
      link.target = '_blank';
      link.click();
    },
  },
};
</script>

<style lang="scss" scoped>
.eie-investements {
  .toolbar {
    position: relative;
    padding: 24px 24px 16px 24px;
  }
  .errorRow {
    width: 98%;
    position: absolute;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    float: left;
  }

  .errorTd {
    padding-bottom: 32px !important;
    .v-icon {
      vertical-align: middle;
    }
  }

  .footer {
    display: flex;
    flex: 1;
    align-items: right;
    flex-direction: row;
    justify-content: flex-start;

    .rows-per-page {
      flex: 0 1 0;
      min-width: 85px;
    }
  }

  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }
label.v-label.v-label--active.theme--dark.primary--text {
    color: white !important;
}
  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  table.v-table tbody td,
  .v-datatable thead th {
      padding: 0 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/eie/investments/EieInvestmentsTable.vue